import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { checkOut, getActiveAttendances, uploadAttendancePhoto } from '@/services/attendance.service';
import { Loader2, LogOut, Search, Clock, User } from 'lucide-react';
import WebcamCapture from './WebcamCapture';

const formatHora = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
};

/**
 * CheckOutModal
 * Modal para registrar la marcación de salida de un empleado.
 * Flujo:
 *  1) Buscar en la lista de marcaciones activas (en turno)
 *  2) Tomar foto
 *  3) Confirmar
 */
const CheckOutModal = ({ isOpen, onClose, onSuccess }) => {
  const { toast } = useToast();

  const [activeAttendances, setActiveAttendances] = useState([]);
  const [loadingAttendances, setLoadingAttendances] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [photoBlob, setPhotoBlob] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Cargar marcaciones activas al abrir
  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoadingAttendances(true);
      try {
        const data = await getActiveAttendances();
        setActiveAttendances(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error al cargar marcaciones activas:', error);
        toast({
          title: 'Error',
          description: 'No se pudieron cargar las marcaciones activas.',
          variant: 'destructive',
        });
      } finally {
        setLoadingAttendances(false);
      }
    };
    load();
    // Reset
    setSelectedAttendance(null);
    setSearchTerm('');
    setPhotoBlob(null);
    setPhotoPreview(null);
  }, [isOpen, toast]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return activeAttendances;
    return activeAttendances.filter((a) => {
      const emp = a.employee || {};
      return (
        String(emp.cedula || '').toLowerCase().includes(q) ||
        String(emp.nombre || '').toLowerCase().includes(q) ||
        String(emp.apellido || '').toLowerCase().includes(q)
      );
    });
  }, [activeAttendances, searchTerm]);

  const handlePhotoCapture = useCallback((blob, dataUrl) => {
    setPhotoBlob(blob);
    setPhotoPreview(dataUrl);
  }, []);

  const handleRetake = useCallback(() => {
    setPhotoBlob(null);
    setPhotoPreview(null);
  }, []);

  const handleSubmit = async () => {
    if (!selectedAttendance) {
      toast({ title: 'Selecciona una marcación', description: 'Búscate en la lista.', variant: 'destructive' });
      return;
    }
    if (!photoBlob) {
      toast({ title: 'Falta la foto', description: 'Tómate una foto como evidencia.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      let photoUrl;
      try {
        photoUrl = await uploadAttendancePhoto(photoBlob);
      } catch (e) {
        console.warn('No se pudo subir la foto, intentando inline:', e);
      }
      const employeeId = selectedAttendance.employee?._id || selectedAttendance.employeeId;
      await checkOut({
        employeeId,
        attendanceId: selectedAttendance._id,
        photoUrl,
        photoBase64: photoPreview,
      });
      toast({
        title: '¡Salida registrada!',
        description: `Hasta pronto, ${selectedAttendance.employee?.nombre}.`,
      });
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error al registrar salida:', error);
      toast({
        title: 'No se pudo registrar la salida',
        description: error.message || 'Intenta de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogOut className="h-5 w-5 text-orange-600" />
            Marcar Salida
          </DialogTitle>
          <DialogDescription>
            Búscate en la lista de personal en turno y tómate una foto.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Paso 1: Lista */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <span className="bg-blue-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs">1</span>
              Búscate en la lista
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cédula, nombre o apellido..."
                className="pl-10 h-11"
              />
            </div>

            {loadingAttendances ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando marcaciones...
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500 border rounded-md bg-gray-50">
                {activeAttendances.length === 0
                  ? 'No hay personal en turno en este momento.'
                  : 'No hay coincidencias para tu búsqueda.'}
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filtered.map((att) => {
                  const emp = att.employee || {};
                  const isSelected = selectedAttendance?._id === att._id;
                  return (
                    <button
                      key={att._id}
                      type="button"
                      onClick={() => setSelectedAttendance(att)}
                      className={`
                        w-full text-left p-3 rounded-md border transition-all
                        ${isSelected
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate">
                            {emp.nombre} {emp.apellido}
                          </div>
                          <div className="text-xs text-gray-500">
                            CC {emp.cedula} {emp.rol ? `· ${emp.rol}` : ''}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                            <Clock className="h-3 w-3" />
                            Entrada
                          </div>
                          <div className="text-sm font-semibold text-gray-900">
                            {formatHora(att.horaEntrada || att.checkInAt || att.createdAt)}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Paso 2: Foto (solo si hay selección) */}
          {selectedAttendance && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <span className="bg-blue-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs">2</span>
                Tómate una foto
              </div>
              {photoPreview ? (
                <div className="space-y-2">
                  <div className="rounded-lg overflow-hidden border bg-gray-50 max-w-md mx-auto">
                    <img src={photoPreview} alt="Foto de salida" className="w-full h-auto" />
                  </div>
                  <div className="flex justify-center">
                    <Button type="button" variant="outline" size="sm" onClick={handleRetake}>
                      Tomar otra foto
                    </Button>
                  </div>
                </div>
              ) : (
                <WebcamCapture label="" onCapture={handlePhotoCapture} />
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedAttendance || !photoBlob || submitting}
            className="bg-orange-600 hover:bg-orange-700 gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Registrando...
              </>
            ) : (
              <>
                <LogOut className="h-4 w-4" />
                Registrar salida
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CheckOutModal;
